import { DateHelper } from "./../helper";
import { EmailType } from "../constants";
import { mailRegexs, mailRegex } from "../constants/mailTemplateConstants";
import { MailService } from "../services/mail/mail.service";
import { useSelector } from "../store/store";

interface ISendMail {
  mailTo: { name: string; email: string; action?: string };
  toPath: string;
  eMailType: EmailType;
  created: { byName: string; dateTime: string };
  aflTypeLabel?: string;
  id?: string;
  description?: string;
}

interface ISendMailChangeApproval {
  mailTo: { name: string; email: string; action?: string };
  toPath: string;
  eMailType: EmailType;
  created: { byName: string; dateTime: string };
  approval: { name: string; email: string };
  id?: string;
  description?: string;
}

interface ISendEmail {
  to: string;
  detailPageUrl: string;
  createdBy: string;
  createdDateTime: string;
  actionText: string;
  eMailType: EmailType;
  aflTypeLabel: string;
  id?: string;
  description?: string;
}

interface ReplaceItem {
  regex: string;
  value: string;
}

export const useMail = () => {
  const mailTemplates = useSelector((state: any) => state.systemMgmt.mailtemplates);

  const sendEmail = async ({
    to = "", // 'email|name'
    detailPageUrl = "",
    createdBy = "", // 'email|name'
    eMailType,
    createdDateTime = "", // date formatted
    aflTypeLabel = "", // date formatted
    actionText = "duyệt",
    id = "",
    description = "",
  }: Partial<ISendEmail>) => {
    const [toEmail = "", toName = ""] = to.split("|");
    const createdByName = createdBy.split("|")[1];

    const template = mailTemplates.find((m: any) => m.eMailType === eMailType);

    const replaceList: ReplaceItem[] = [
      { regex: mailRegex.zero, value: toName },
      { regex: mailRegex.one, value: actionText },
      { regex: mailRegex.two, value: createdByName },
      { regex: mailRegex.three, value: createdDateTime },
      { regex: mailRegex.four, value: aflTypeLabel },
      { regex: mailRegex.nine, value: id },
      { regex: mailRegex.ten, value: description },
      { regex: mailRegex.siteUrl, value: detailPageUrl },
    ];
    const replaceSubjectList: ReplaceItem[] = [{ regex: mailRegex.zero, value: aflTypeLabel }];
    const content = replaceList.reduce(
      (acc: string, item: ReplaceItem) => acc.replace(item.regex, item.value),
      template.eContent,
    );

    const subject = replaceSubjectList.reduce(
      (acc: string, item: ReplaceItem) => acc.replace(item.regex, item.value),
      template.eSubject,
    );

    const message = MailService.getMessage({
      subject,
      contentType: "html",
      content,
      address: toEmail,
    });

    await MailService.sendMail(message);
  };

  const sendMail = async ({ mailTo, toPath, eMailType, created, aflTypeLabel, id, description }: ISendMail) => {
    const template = mailTemplates.find((m: any) => m.eMailType === eMailType);
    const content = template.eContent
      .replace(mailRegexs.zero + ";", mailTo.name)
      .replace(mailRegexs.one + ";", mailTo.action === undefined ? "duyệt" : mailTo.action)
      .replace(mailRegexs.two + ";", created.byName)
      .replace(mailRegexs.three + ";", created.dateTime)
      .replace(mailRegexs.four + ";", aflTypeLabel)
      .replace(mailRegexs.nine + ";", id)
      .replace(mailRegexs.ten + ";", description ?? "")
      .replace(mailRegexs.siteUrl, toPath);

    const subject = template.eSubject.replace("{0}", aflTypeLabel);
    const message = MailService.buildMailMesssage(subject, content, "html", mailTo.email);
    await MailService.sendMail(message);
  };

  const sendMailChangeApproval = async ({ mailTo, toPath, eMailType, created, approval, id, description }: ISendMailChangeApproval) => {
    const template = mailTemplates.find((m: any) => m.eMailType === eMailType);
    const content = template.eContent
      .replace(mailRegexs.zero + ";", mailTo.name)
      .replace(mailRegexs.one + ";", `${approval.name} (${approval.email})`)
      .replace(mailRegexs.two + ";", created.byName)
      .replace(mailRegexs.three + ";", created.dateTime)
      .replace(mailRegexs.nine + ";", id)
      .replace(mailRegexs.ten + ";", description ?? "")
      .replace(mailRegexs.siteUrl, toPath);
    const message = MailService.buildMailMesssage(template.eSubject, content, "html", mailTo.email);
    await MailService.sendMail(message);
  };

  const sendMailApproval = ({
    request,
    step,
    statusMail,
    detailPageUrl,
  }: {
    request: any;
    step: any;
    statusMail: "approvedMail" | "rejectedMail";
    detailPageUrl: string;
  }) => {
    Promise.all(
      step[statusMail].to.map(async (toField: string) => {
        const fieldValue = request[toField];
        sendMail({
          mailTo: {
            name: fieldValue.split("|")[1],
            email: fieldValue.split("|")[0],
          },
          toPath: detailPageUrl,
          eMailType: step[statusMail].type as EmailType,
          created: {
            byName: request.eCreatedBy.split("|")[1],
            dateTime: DateHelper.formatDateToString(request.createdDateTime),
          },
          id: request.id,
          description: request.description
        });
      }),
    );
  };

  return { sendMail, sendMailApproval, sendEmail, sendMailChangeApproval };
};
